// @flow

import React, { Component } from 'react';
import * as StringUtil from 'app/common/lib/StringUtil';
import i18n from 'app/common/lib/i18n';
import { Spinner } from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/common';
import DidItContent, {
  type PinActivity,
} from 'app/common/react/components/do/aggregated-pins/did-it/DidItRecommend/DidItContent';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Divider, Heading } from 'gestalt';

type PreFetchedPinActivityData = {
  id: string,
  details: string,
  images: Array<{ '200x': { url: string } }>,
  image_signatures: Array<string>,
};

type OwnProps = {|
  aggregatedPinDataId: string,
  isDoneByMe: boolean,
  loggedInUser: {
    username: string,
    id: string,
  },
  onDoneStateChanged?: (stateChanged: boolean) => void, // indicates if isDoneByMe has changed
  pinId: string,
  pinTitle: ?string,
  shareToFacebookCheckedOn: boolean,
  shareToFacebookPermissionGranted: boolean,
  userDidItDataId?: string,
|};

type HOCProps = {|
  pinActivityResource?: Resource<PreFetchedPinActivityData>,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
|};

// Make the fetched Pin Activity match with the defined type, if it exists.
function sanitizePinActivity(pinActivityData: ?PreFetchedPinActivityData): ?PinActivity {
  if (!pinActivityData) {
    return null;
  }

  const { details, images, image_signatures, id } = pinActivityData;
  return {
    id,
    imageSignatures: image_signatures,
    notes: details,
    oldImageUrl: images[0]['200x'].url,
  };
}

export class DidItRecommend extends Component<Props> {
  onDoneStateChanged = (stateChanged: boolean) => {
    const { onDoneStateChanged, pinActivityResource } = this.props;
    if (onDoneStateChanged) {
      onDoneStateChanged(stateChanged);
    }

    if (stateChanged && pinActivityResource && pinActivityResource.refreshData) {
      pinActivityResource.refreshData();
    }
  };

  render() {
    const {
      onDoneStateChanged,
      pinActivityResource,
      pinTitle,
      userDidItDataId,
      ...rest
    } = this.props;

    return !pinActivityResource || (pinActivityResource && pinActivityResource.isLoaded) ? (
      <Box columns={12} shape="rounded">
        <Box alignItems="center" display="flex" justifyContent="center" paddingY={5}>
          <Heading size="xs">
            {pinTitle
              ? StringUtil.dangerouslyFormat(
                  i18n._('Add a photo of {{pinTitle}}', 'Adding photo of a pin'),
                  {
                    pinTitle,
                  },
                )
              : i18n._('Add a photo', 'Adding a photo')}
          </Heading>
        </Box>
        <Divider />
        <DidItContent
          onDoneStateChanged={this.onDoneStateChanged}
          pinActivity={sanitizePinActivity(pinActivityResource && pinActivityResource.data)}
          {...rest}
        />
      </Box>
    ) : (
      <Spinner />
    );
  }
}

const FetchDidItRecommendAggregatedData = withResource(
  {
    name: 'DidItUserPinActivityResource',
    key: 'pinActivityResource',
    options: ({ aggregatedPinDataId }) => ({
      aggregated_pin_data_id: aggregatedPinDataId,
      username: 'me',
    }),
  },
  { allowStale: false },
)(DidItRecommend);

const FetchDidItRecommendUserDidItData = withResource(
  {
    name: 'DidItActivityResource',
    key: 'pinActivityResource',
    options: ({ userDidItDataId }) => ({
      user_did_it_data_id: userDidItDataId,
      username: 'me',
    }),
  },
  { allowStale: false },
)(DidItRecommend);

export default function(props: OwnProps) {
  if (props.isDoneByMe && props.userDidItDataId) {
    return <FetchDidItRecommendUserDidItData {...props} />;
  } else if (props.isDoneByMe) {
    return <FetchDidItRecommendAggregatedData {...props} />;
  }
  return <DidItRecommend {...props} />;
}
